"use client";
import { FormField, FormItem, FormMessage } from "@/ui/form";
import {
  getMaxDateAllowedToReserve,
  getMinDateAllowedToReserve,
  getTimeForReservation
} from "@/lib/utils";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { Office, ResourceType } from "@gettactic/api";
import { useMemo } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { DateValue } from "@cmp-src/calendar/types";

type ReserveStartDateProps = {
  form: UseFormReturn<any>;
  defaultValue: Date[];
  startTime: Date;
  officeTimeZone: string;
  weekDates: Date[];
  office?: Office;
  resType: ResourceType;
  errors: FieldErrors<SaveReservationData>;
  isAdmin?: boolean;
  extraDays?: number;
};

export function ReserveFieldStartDate({
  form,
  defaultValue,
  startTime,
  weekDates,
  officeTimeZone,
  office,
  resType,
  errors,
  isAdmin,
  extraDays
}: ReserveStartDateProps) {
  const minAllowedReserveDate = useMemo(
    () => getMinDateAllowedToReserve(officeTimeZone, weekDates),
    [officeTimeZone, weekDates]
  );
  const maxAllowedReserveDate = useMemo(
    () =>
      getMaxDateAllowedToReserve(officeTimeZone, office, resType, extraDays),
    [office, officeTimeZone, resType, extraDays]
  );

  const disabledDates = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minAllowedReserveDate) {
      matchers.push({ before: minAllowedReserveDate });
    }
    if (maxAllowedReserveDate && !isAdmin) {
      matchers.push({ after: maxAllowedReserveDate });
    }
    return matchers.length > 0 ? matchers : undefined;
  }, [minAllowedReserveDate, maxAllowedReserveDate]);

  return (
    <>
      <FormField
        control={form.control}
        name="weekDates"
        render={({ field }) => (
          <CalendarInput
            mode="single"
            inputClassName="!border-none rounded-v2 bg-neutral-50 text-center font-mona font-normal"
            selectedDate={field.value[0]}
            showDayName={false}
            onSelect={(date: DateValue | undefined) => {
              if (date instanceof Date) {
                const newTime = getTimeForReservation(
                  date,
                  startTime,
                  office || null
                );
                form.setValue("time", newTime);
                form.setValue("weekDates", [date], {
                  shouldValidate: true
                });
              }
            }}
            disabled={disabledDates}
            withArrows={false}
          />
        )}
      />
      {errors.weekDates ? (
        <p className="text-primary">Please choose a valid date</p>
      ) : null}
    </>
  );
}
